import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

/** Hộp thư Zalo cá nhân (CR-014 bước 1). Realtime: socket 'zalo:changed' làm mới mọi query ['zalo', …]. */

/** CR-014 bước 2: mỗi "nick" là 1 kênh — Zalo cá nhân, Zalo OA, Fanpage Messenger (Website ở bước 3). */
export type InboxChannel = 'zalo' | 'zalo_oa' | 'messenger' | 'web' | 'email' | 'phone';
export const CHANNEL_LABELS: Record<InboxChannel, string> = {
  zalo: 'Zalo',
  zalo_oa: 'Zalo OA',
  messenger: 'Messenger',
  web: 'Website',
  email: 'Email',
  phone: 'Tổng đài',
};

export type ZaloStatus = 'disconnected' | 'connecting' | 'connected' | 'needs_login' | 'error';
export interface ZaloAccount {
  id: number;
  channel: InboxChannel;
  name: string;
  ownId: string | null;
  displayName: string | null;
  avatarUrl: string | null;
  phone: string | null;
  status: ZaloStatus;
  lastError: string | null;
  lastConnectedAt: string | null;
  ownerUserId: number;
  ownerName: string;
  hasSession: boolean;
  /** Nick Zalo cá nhân của NV (DEC-662): GĐ / TP chỉ thấy hội thoại đã gắn khách / NCC */
  isPersonal: boolean;
  unread: number;
}
export const ZALO_STATUS_LABELS: Record<ZaloStatus, string> = {
  connected: 'Đang kết nối',
  connecting: 'Đang đăng nhập',
  disconnected: 'Đã ngắt',
  needs_login: 'Cần quét QR lại',
  error: 'Lỗi',
};

export interface ZaloConversation {
  id: number;
  channel: InboxChannel;
  accountId: number;
  accountName: string;
  ownerUserId: number;
  status: 'open' | 'closed';
  unreadCount: number;
  lastMessageText: string | null;
  lastDirection: 'in' | 'out' | null;
  lastMessageAt: string;
  assignedUserId: number | null;
  assignedName: string | null;
  contactId: number;
  threadId: string;
  threadType: 'user' | 'group';
  contactName: string | null;
  avatarUrl: string | null;
  phone: string | null;
  friendStatus: string | null;
  linkSource: 'auto' | 'manual' | null;
  /** Người / nhóm nội bộ công ty: trợ lý tự động không trả lời */
  isInternal: boolean;
  /** Gán là Nhân viên CTY theo danh sách nhân sự (15/10/2026) */
  employeeId: number | null;
  employeeName: string | null;
  customerId: number | null;
  customerCode: string | null;
  customerName: string | null;
  /** Chat riêng của nick cá nhân (chưa gắn khách / NCC): chỉ chủ nick thấy, xóa được khỏi ERP (DEC-662) */
  isPrivate: boolean;
  /** Nhóm Zalo nhiều nick công ty cùng tham gia: các bản hội thoại (mỗi nick 1 bản), nick của mình trước. */
  via?: ConversationVia[];
}
export interface ConversationVia {
  id: number;
  accountId: number;
  accountName: string;
  ownerUserId: number;
}

/** Kênh công ty dùng chung (không phải nick / email riêng của NV); khớp SHARED_CHANNELS ở API. */
export const SHARED_CHANNELS: readonly InboxChannel[] = ['zalo_oa', 'messenger', 'web', 'phone'];

export interface ZaloAttachment {
  type: string;
  url: string | null;
  name: string | null;
  thumbnail: string | null;
  size: number | null;
}
export interface ZaloMessage {
  id: number;
  direction: 'in' | 'out';
  messageType: string;
  text: string | null;
  attachments: ZaloAttachment[];
  senderName: string | null;
  sentByUserId: number | null;
  sentByName: string | null;
  quoteId: number | null;
  quoteCode: string | null;
  sentAt: string;
}

export interface QrLoginState {
  stage: 'none' | 'waiting' | 'scanned' | 'expired' | 'declined' | 'connected' | 'error';
  image: string | null;
  displayName: string | null;
  error: string | null;
  account: ZaloAccount;
}

export interface SendableQuote {
  id: number;
  code: string;
  status: string;
  quoteDate: string;
  grandTotal: number;
  version: number;
  projectName: string | null;
}

export interface ConversationFilter {
  accountId?: number;
  q?: string;
  unread?: boolean;
  mine?: boolean;
  /** 'mine' = nick của mình + kênh chung + hội thoại được giao */
  scope?: 'mine';
  threadType?: 'user' | 'group';
}

const PAGE = 30;
const MSG_PAGE = 40;

export const useZaloAccounts = (enabled = true) =>
  useQuery({
    queryKey: ['zalo', 'accounts'],
    queryFn: () => api.get<{ items: ZaloAccount[] }>('/zalo/accounts').then((r) => r.items),
    enabled,
    refetchInterval: 60_000,
  });

export const useZaloConversations = (f: ConversationFilter, enabled = true) =>
  useInfiniteQuery({
    queryKey: ['zalo', 'conversations', f],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.get<{ items: ZaloConversation[]; hasMore: boolean }>('/zalo/conversations', {
        accountId: f.accountId,
        q: f.q?.trim() || undefined,
        unread: f.unread || undefined,
        mine: f.mine || undefined,
        scope: f.scope,
        threadType: f.threadType,
        before: pageParam,
        limit: PAGE,
      }),
    getNextPageParam: (last) => (last.hasMore ? last.items[last.items.length - 1]?.lastMessageAt : undefined),
    placeholderData: keepPreviousData,
    // Realtime chỉ báo chủ nick / NV được giao / GĐ; TP xem nick của NV thì tự làm mới mỗi phút
    refetchInterval: 60_000,
    enabled,
  });

export const useZaloConversation = (id: number | null) =>
  useQuery({ queryKey: ['zalo', 'conversation', id], queryFn: () => api.get<ZaloConversation>(`/zalo/conversations/${id}`), enabled: !!id });

export const useZaloMessages = (id: number | null) =>
  useInfiniteQuery({
    queryKey: ['zalo', 'messages', id],
    initialPageParam: undefined as number | undefined,
    queryFn: ({ pageParam }) =>
      api.get<{ items: ZaloMessage[]; hasMore: boolean }>(`/zalo/conversations/${id}/messages`, { before: pageParam, limit: MSG_PAGE }),
    // Trang sau = tin cũ hơn tin đầu tiên của trang trước
    getNextPageParam: (last) => (last.hasMore ? last.items[0]?.id : undefined),
    enabled: !!id,
  });

export const useSendableQuotes = (id: number | null, enabled: boolean) =>
  useQuery({
    queryKey: ['zalo', 'quotes', id],
    queryFn: () => api.get<{ customerId: number | null; items: SendableQuote[] }>(`/zalo/conversations/${id}/quotes`),
    enabled: !!id && enabled,
  });

export const getQrLogin = (accountId: number) => api.get<QrLoginState>(`/zalo/accounts/${accountId}/login-qr`);

/** Mọi thao tác ghi: xong thì làm mới cả nhánh ['zalo'] (danh sách, tin, nick). */
function useZaloMutation<V, R = unknown>(fn: (v: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSuccess: () => qc.invalidateQueries({ queryKey: ['zalo'] }) });
}

export const useCreateAccount = () => useZaloMutation((name: string) => api.post<ZaloAccount>('/zalo/accounts', { name }));
export const useRemoveAccount = () => useZaloMutation((id: number) => api.del(`/zalo/accounts/${id}`));
export const useStartQr = () => useZaloMutation((id: number) => api.post(`/zalo/accounts/${id}/login-qr`));
export const useCancelQr = () => useZaloMutation((id: number) => api.post(`/zalo/accounts/${id}/login-qr/cancel`));
export const useConnectAccount = () => useZaloMutation((id: number) => api.post(`/zalo/accounts/${id}/connect`));
export interface EmailConnectBody {
  name?: string;
  email: string;
  password: string;
  provider: 'gmail' | 'outlook' | 'zoho' | 'custom';
  imapHost?: string;
  imapPort?: number;
  smtpHost?: string;
  smtpPort?: number;
}
/** Bước 7a (DEC-232): NV kết nối hộp thư email của mình (mật khẩu ứng dụng). */
export const useConnectEmail = () => useZaloMutation((b: EmailConnectBody) => api.post<{ id: number }>('/zalo/channels/email', b));
export const useDisconnectAccount = () => useZaloMutation((id: number) => api.post(`/zalo/accounts/${id}/disconnect`));
/** DEC-662: chủ nick đánh dấu nick cá nhân / nick công ty */
export const useSetPersonal = () =>
  useZaloMutation((v: { id: number; personal: boolean }) => api.put<ZaloAccount>(`/zalo/accounts/${v.id}/personal`, { personal: v.personal }));
/** DEC-662: chủ nick cá nhân xóa chat riêng khỏi ERP */
export const useDeletePrivateConversation = () => useZaloMutation((id: number) => api.del(`/zalo/conversations/${id}`));

export interface PhoneLookup {
  phone: string;
  uid: string;
  name: string | null;
  avatarUrl: string | null;
  customerId: number | null;
}
export const lookupPhone = (accountId: number, phone: string) => api.post<PhoneLookup>(`/zalo/accounts/${accountId}/lookup`, { phone });
export const useOpenByPhone = () =>
  useZaloMutation((v: { accountId: number; phone: string; friendRequest: boolean; greeting?: string | null }) =>
    api.post<ZaloConversation>(`/zalo/accounts/${v.accountId}/open`, { phone: v.phone, friendRequest: v.friendRequest, greeting: v.greeting }),
  );

export const useMarkRead = () => useZaloMutation((id: number) => api.post(`/zalo/conversations/${id}/read`));
export const useSetStatus = () =>
  useZaloMutation((v: { id: number; status: 'open' | 'closed' }) => api.put(`/zalo/conversations/${v.id}/status`, { status: v.status }));
export const useAssign = () =>
  useZaloMutation((v: { id: number; userId: number | null }) => api.put(`/zalo/conversations/${v.id}/assign`, { userId: v.userId }));
export const useLinkCustomer = () =>
  useZaloMutation((v: { id: number; customerId: number | null }) => api.put(`/zalo/conversations/${v.id}/customer`, { customerId: v.customerId }));
export const useSetInternal = () =>
  useZaloMutation((v: { id: number; internal: boolean; employeeId?: number | null }) =>
    api.put(`/zalo/conversations/${v.id}/internal`, { internal: v.internal, employeeId: v.employeeId ?? null }),
  );
export const useStaffOptions = (enabled: boolean) =>
  useQuery({
    queryKey: ['zalo', 'staff-options'],
    queryFn: () => api.get<{ items: { id: number; code: string; fullName: string }[] }>('/zalo/staff-options'),
    enabled,
    staleTime: 5 * 60_000,
  });
export const useSendText = () => useZaloMutation((v: { id: number; text: string }) => api.post(`/zalo/conversations/${v.id}/messages`, { text: v.text }));
export const useSendFile = () =>
  useZaloMutation((v: { id: number; file: File; caption?: string }) => {
    const form = new FormData();
    if (v.caption) form.append('caption', v.caption);
    form.append('file', v.file);
    return api.upload(`/zalo/conversations/${v.id}/files`, form);
  });
export const useSendQuote = () =>
  useZaloMutation((v: { id: number; quoteId: number; version: number; message?: string | null }) =>
    api.post<{ ok: true; quoteCode: string }>(`/zalo/conversations/${v.id}/send-quote`, { quoteId: v.quoteId, version: v.version, message: v.message }),
  );

// P24 PR 1b (DEC-459): gợi ý + gửi CO/CQ hàng hóa (bản sao đóng chữ) vào hội thoại
export interface InboxCert {
  id: number;
  kind: string;
  kindLabel: string;
  title: string;
  docNumber: string | null;
  brandName: string | null;
  manufacturer: string | null;
  lotNo: string | null;
  issuedOn: string | null;
  expired: boolean;
  products: { id: number; code: string; name: string }[];
}
export const useInboxCertificates = (id: number | null, q: string, enabled: boolean) =>
  useQuery({
    queryKey: ['zalo', 'certificates', id, q],
    queryFn: () =>
      api.get<{ customerId: number | null; customerName: string | null; suggested: boolean; items: InboxCert[] }>(
        `/zalo/conversations/${id}/certificates`,
        q.trim() ? { q: q.trim() } : undefined,
      ),
    enabled: !!id && enabled,
  });
export const useSendCertificates = () =>
  useZaloMutation((v: { id: number; certificateIds: number[]; message?: string | null }) =>
    api.post<{ ok: true; files: string[] }>(`/zalo/conversations/${v.id}/send-certificates`, { certificateIds: v.certificateIds, message: v.message }),
  );

export interface ChannelSettings {
  zaloOa: { configured: boolean; callbackUrl: string; webhookUrl: string; webhookSigned: boolean };
  messenger: { configured: boolean; callbackUrl: string; webhookUrl: string; verifyToken: string | null };
}
export const useChannelSettings = (enabled: boolean) =>
  useQuery({ queryKey: ['zalo', 'channels'], queryFn: () => api.get<ChannelSettings>('/zalo/channels'), enabled, staleTime: 5 * 60_000 });
/** Kết nối Zalo OA / Fanpage: lấy link đăng nhập rồi chuyển trang; xong Zalo / Facebook đưa về ERP kèm ?hop-thu=… */
export const startChannelConnect = (kind: 'zalo-oa' | 'messenger') => api.post<{ url: string }>(`/zalo/channels/${kind}/connect`);

export interface WebChatSettings {
  key: string;
  origins: string[];
  greeting: string;
  askContact: boolean;
}
export interface WebChatInput {
  name: string;
  origins: string[];
  greeting?: string | null;
  askContact?: boolean;
}
export const useWebChat = (id: number | null) =>
  useQuery({ queryKey: ['zalo', 'web', id], queryFn: () => api.get<WebChatSettings>(`/zalo/channels/web/${id}`), enabled: !!id });
export const useCreateWebChat = () => useZaloMutation((v: WebChatInput) => api.post<{ id: number; key: string }>('/zalo/channels/web', v));
export const useUpdateWebChat = () => useZaloMutation((v: WebChatInput & { id: number }) => api.put(`/zalo/channels/web/${v.id}`, v));
/** Dòng script website cần dán (trước thẻ </body>). */
export const embedCode = (key: string) => `<script src="${window.location.origin}/api/v1/public/webchat/${key}/widget.js" async></script>`;

/** Bước 7b (DEC-233): tóm tắt công việc hằng ngày – chỉ bản của mình (TP / GĐ: kèm bảng NV trong phạm vi). */
export interface SummaryNv {
  userId: number;
  name: string;
  accounts: number;
  messagesIn: number;
  customers: number;
  replied: number;
  messagesOut: number;
  quotesSent: number;
  botMessages: number;
  quotesCreated: number;
  quotesValue: number;
  botDrafts: number;
  followupsDue: number;
  followupsTomorrow: number;
  waitingCount: number;
  waiting: { conversationId: number; name: string; channel: InboxChannel; since: string }[];
}
export interface DailySummary {
  id: number;
  date: string;
  scope: 'self' | 'department' | 'company';
  stats: { date: string; self: SummaryNv; team: SummaryNv[] | null };
  notes: { comment: string | null; tomorrow: string[] };
  text: string;
  model: string | null;
  createdAt: string;
  updatedAt: string;
}
export const useSummaries = (enabled: boolean) =>
  useQuery({ queryKey: ['zalo', 'summaries'], queryFn: () => api.get<{ items: DailySummary[] }>('/zalo/summaries').then((r) => r.items), enabled });
export const useRunSummary = () => useZaloMutation<void, DailySummary>(() => api.post<DailySummary>('/zalo/summaries/run'));

/** Bước 6 (DEC-234): tổng đài Stringee – nhật ký cuộc gọi vào hộp thư, NV gọi khách qua tổng đài. */
export interface PhoneSettings {
  configured: boolean;
  number: string | null;
  connected: boolean;
  lastSyncAt: string | null;
  lastError: string | null;
}
export const usePhoneSettings = (enabled = true) =>
  useQuery({ queryKey: ['zalo', 'phone'], queryFn: () => api.get<PhoneSettings>('/zalo/channels/phone'), enabled, staleTime: 5 * 60_000 });
export const useConnectPhone = () => useZaloMutation<void, PhoneSettings>(() => api.post<PhoneSettings>('/zalo/channels/phone'));
export const useCallCustomer = () => useZaloMutation((id: number) => api.post<{ id: number; message: string }>(`/zalo/conversations/${id}/call`));
